import { Button } from '@carbon/react';
import {
  CheckmarkOutline,
  Clean,
  Close,
  Information,
  OrderDetails,
  Restart,
  SettingsAdjust,
  TrashCan,
  Undo,
} from '@carbon/react/icons';
import { StatusTag } from 'components/status-tag';
import { BlockedAction } from './blocked-action';
import type { PartyState } from './party-status';
import type { StructuredPartyDetail } from './types';
import { anchorSourceLabel } from './utils';
import style from './party-detail.module.scss';

interface PartyHeaderProps {
  title: string;
  detail: StructuredPartyDetail;
  state: PartyState;
  pending: boolean;
  rerunPending: boolean;
  changeMatchOpen: boolean;
  undoReconcileAvailable: boolean;
  hasMatches: boolean;
  hasSelectedMatch: boolean;
  onRestore: () => void;
  onToggleGlossary: () => void;
  onUndoReconcile: () => void;
  onRevert: () => void;
  onRemove: () => void;
  onRerun: () => void;
  onToggleChangeMatch: () => void;
  onReconcile: () => void;
  onToggleAgentDetails: () => void;
  onMarkCorrect: () => void;
}

interface RemoveCorrectStatusActionProps {
  allowed: boolean;
  disabledReason: string | null;
  pending: boolean;
  onRevert: () => void;
}

/**
 * Remove Correct Status, or, when the server withholds it, a BlockedAction
 * with the server's reason.
 *
 * @param {RemoveCorrectStatusActionProps} props - Whether it is allowed, the server's reason when not, and the action.
 * @return {JSX.Element} The action.
 */
function RemoveCorrectStatusAction({
  allowed,
  disabledReason,
  pending,
  onRevert,
}: RemoveCorrectStatusActionProps) {
  if (allowed || !disabledReason) {
    return (
      <Button
        kind="tertiary"
        size="sm"
        renderIcon={Close}
        onClick={onRevert}
        disabled={pending || !allowed}
      >
        Remove Correct Status
      </Button>
    );
  }

  return (
    <BlockedAction
      kind="tertiary"
      label="Remove Correct Status"
      reason={disabledReason}
      renderIcon={Close}
      placement="bottom"
    />
  );
}

/**
 * The party detail header: title, status and the party's actions.
 *
 * @param {PartyHeaderProps} props - The party, its state, and the action handlers.
 * @return {JSX.Element} The header.
 */
export function PartyHeader({
  title,
  detail,
  state,
  pending,
  rerunPending,
  changeMatchOpen,
  undoReconcileAvailable,
  hasMatches,
  hasSelectedMatch,
  onRestore,
  onToggleGlossary,
  onUndoReconcile,
  onRevert,
  onRemove,
  onRerun,
  onToggleChangeMatch,
  onReconcile,
  onToggleAgentDetails,
  onMarkCorrect,
}: PartyHeaderProps) {
  const {
    isRemoved,
    isUserAdded,
    hasUnresolvedAddressFlag,
    matchLocked,
    canRevert,
    revertAllowed,
    revertDisabledReason,
    canRemove,
    canReconcile,
  } = state;
  const anchorLabel = anchorSourceLabel(detail.currentAnchor?.source, 'inline');

  return (
    <header className={style.header}>
      <div className={style.titleBlock}>
        <h4 className={style.partyTitle}>{title}</h4>
        <span className={style.statusTagSlot}>
          <StatusTag status={detail.status} />
        </span>
      </div>
      <div className={style.actionBar}>
        {isRemoved && (
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Undo}
            onClick={onRestore}
            disabled={pending}
          >
            Undo Remove
          </Button>
        )}
        {!isRemoved && (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Information}
            iconDescription="Scoring Glossary"
            tooltipPosition="bottom"
            tooltipAlignment="end"
            onClick={onToggleGlossary}
          />
        )}
        {undoReconcileAvailable && (
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Undo}
            onClick={onUndoReconcile}
            disabled={pending}
          >
            Undo Reconcile
          </Button>
        )}
        {canRevert && (
          <RemoveCorrectStatusAction
            allowed={revertAllowed}
            disabledReason={revertDisabledReason}
            pending={pending}
            onRevert={onRevert}
          />
        )}
        {canRemove && (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={TrashCan}
            iconDescription="Remove Party"
            tooltipPosition="bottom"
            tooltipAlignment="end"
            onClick={onRemove}
            disabled={pending}
            className={style.removeButton}
          />
        )}
        {!matchLocked && (
          <>
            <Button
              kind="tertiary"
              size="sm"
              hasIconOnly
              renderIcon={Restart}
              iconDescription={
                rerunPending ? 'Matching is running…' : 'Re-run Match'
              }
              tooltipPosition="bottom"
              tooltipAlignment="end"
              onClick={onRerun}
              disabled={pending || rerunPending}
            />
            <Button
              kind={changeMatchOpen ? 'secondary' : 'tertiary'}
              size="sm"
              hasIconOnly
              renderIcon={changeMatchOpen ? Close : SettingsAdjust}
              iconDescription={changeMatchOpen ? 'Close' : 'Change/Edit Match'}
              tooltipPosition="bottom"
              tooltipAlignment="end"
              onClick={onToggleChangeMatch}
              disabled={pending}
            />
          </>
        )}
        {canReconcile && (
          <Button
            kind="tertiary"
            size="sm"
            hasIconOnly
            renderIcon={Clean}
            iconDescription={
              hasUnresolvedAddressFlag
                ? `Resolve the ${anchorLabel}’s review flag before reconciling`
                : 'Reconcile'
            }
            tooltipPosition="bottom"
            tooltipAlignment="end"
            onClick={onReconcile}
            disabled={pending || hasUnresolvedAddressFlag}
          />
        )}
        {!matchLocked && (
          <>
            {!isUserAdded && (
              <Button
                kind="tertiary"
                size="sm"
                hasIconOnly
                renderIcon={OrderDetails}
                iconDescription={
                  detail.agentDetails
                    ? 'Match Details'
                    : 'Match Details unavailable — no completed match run for this party yet.'
                }
                tooltipPosition="bottom"
                tooltipAlignment="end"
                onClick={onToggleAgentDetails}
                disabled={!detail.agentDetails}
              />
            )}
            {hasMatches && (
              <Button
                kind="primary"
                size="sm"
                renderIcon={CheckmarkOutline}
                onClick={onMarkCorrect}
                disabled={pending || !hasSelectedMatch}
              >
                Mark as Correct
              </Button>
            )}
          </>
        )}
      </div>
    </header>
  );
}
